import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { getJson } from "../api";
import { formatText, useTexts } from "../texts";
import { ChatIcon } from "./icons/Icon";
import { pageScroller } from "./scroll";
import styles from "./list.module.css";

// Listas del Admin (C28 escritorio, C17 celular; D16, §7.1): el servidor filtra, ordena y pagina y devuelve
// { items, total, page, pageSize }. Página y tamaño viven en la dirección (E84); el contador, el rango y el paginado salen
// del mismo total (V24, E100). En el celular la lista carga al bajar, sin paginado.

export type Page<T> = { items: T[]; total: number; page: number; pageSize: number };

export const pageSizes = [10, 25, 50] as const;

export type ServerList<T> = {
  state: "loading" | "ready" | "error" | "forbidden" | "notFound";
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  /** La última respuesta entera: datos que vienen con la página (p. ej., los conteos de las píldoras). */
  extra: Record<string, unknown> | null;
  /** Celular: quedan más por traer al bajar. */
  more: boolean;
  loadingMore: boolean;
  reload: () => void;
  loadMore: () => void;
  /** Nombre del parámetro de página en la dirección ("page"; otro cuando una pantalla tiene dos listas). */
  pageParam: string;
};

const mobile = () => window.matchMedia("(max-width: 767px)").matches;

/** Primera página que ya trajo otra lectura (la ficha que la pide junto con su encabezado), para esa misma `url`. */
export type InitialPage<T> = { url: string; page: Page<T> };

/**
 * Lee una lista paginada del BFF con la página y el tamaño de la dirección. `url` ya trae sus filtros (o null: sin pedir).
 * Con `initial` (la misma `url`, su página ya leída) arranca con ella sin volver a pedirla; después pide como siempre.
 */
export function useServerList<T>(
  url: string | null,
  pageParam = "page",
  initial: InitialPage<T> | null = null,
): ServerList<T> {
  const [params] = useSearchParams();
  const page = Math.max(1, Number(params.get(pageParam)) || 1);
  const sizeParam = Number(params.get("pageSize"));
  const pageSize = (pageSizes as readonly number[]).includes(sizeParam) ? sizeParam : 10;
  const [state, setState] = useState<ServerList<T>["state"]>(initial ? "ready" : "loading");
  const [items, setItems] = useState<T[]>(initial?.page.items ?? []);
  const [total, setTotal] = useState(initial?.page.total ?? 0);
  const [extra, setExtra] = useState<Record<string, unknown> | null>(
    initial ? (initial.page as unknown as Record<string, unknown>) : null,
  );
  const [loaded, setLoaded] = useState(initial?.page.page ?? 1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [version, setVersion] = useState(0);
  const request = useRef(0);
  // La página inicial vale una sola vez y solo para su dirección; después (otra página, otro filtro, recargar) se pide.
  const seeded = useRef(initial?.url ?? null);

  const fetchPage = useCallback(
    async (number: number) => {
      const separator = url!.includes("?") ? "&" : "?";
      return getJson<Page<T>>(`${url}${separator}page=${number}&pageSize=${pageSize}`);
    },
    [url, pageSize],
  );

  useEffect(() => {
    if (!url) return;
    if (seeded.current !== null) {
      const same = seeded.current === url;
      seeded.current = null;
      if (same) return;
    }
    const current = ++request.current;
    // En el celular siempre se arranca por la primera página y se suma al bajar.
    const first = mobile() ? 1 : page;
    void fetchPage(first).then((reply) => {
      if (current !== request.current) return;
      if (reply.status === 200 && reply.body) {
        setItems(reply.body.items);
        setTotal(reply.body.total);
        setExtra(reply.body as unknown as Record<string, unknown>);
        setLoaded(first);
        setState("ready");
      } else setState(reply.status === 403 ? "forbidden" : reply.status === 404 ? "notFound" : "error");
    });
  }, [url, page, fetchPage, version]);

  const loadMore = useCallback(() => {
    if (loadingMore || items.length >= total) return;
    const next = loaded + 1;
    const current = request.current;
    setLoadingMore(true);
    void fetchPage(next).then((reply) => {
      setLoadingMore(false);
      if (current !== request.current || reply.status !== 200 || !reply.body) return;
      setItems((previous) => [...previous, ...reply.body!.items]);
      setTotal(reply.body.total);
      setLoaded(next);
    });
  }, [loadingMore, items.length, total, loaded, fetchPage]);

  return {
    state,
    items,
    total,
    page,
    pageSize,
    extra,
    more: items.length < total,
    loadingMore,
    reload: () => setVersion((value) => value + 1),
    loadMore,
    pageParam,
  };
}

/** Orden de una tabla (C28): la columna y si va de mayor a menor (o de lo más reciente a lo más antiguo). */
export type SortState = { key: string; desc: boolean } | null;

/**
 * C28 · el orden viaja en la dirección (`sort` y `dir`) y lo aplica el servidor sobre todos los registros. Solo vale una
 * columna de `allowed`; cambiarlo vuelve a la primera página sin llenar el historial. `query` va tal cual al BFF.
 */
export function useSort(allowed: readonly string[]) {
  const [params, setParams] = useSearchParams();
  const key = params.get("sort");
  const sort: SortState = key && allowed.includes(key) ? { key, desc: params.get("dir") === "desc" } : null;
  const set = (next: SortState) => {
    const copy = new URLSearchParams(params);
    if (next) {
      copy.set("sort", next.key);
      copy.set("dir", next.desc ? "desc" : "asc");
    } else {
      copy.delete("sort");
      copy.delete("dir");
    }
    copy.delete("page");
    setParams(copy, { replace: true });
  };
  return { sort, set, query: sort ? `sort=${sort.key}&dir=${sort.desc ? "desc" : "asc"}` : "" };
}

/** Pie de la lista: paginado en escritorio (C28) y carga al bajar en el celular (C17). */
export function ListFooter<T>({ list }: { list: ServerList<T> }) {
  const [isMobile, setIsMobile] = useState(mobile);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const change = () => setIsMobile(query.matches);
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);
  if (list.state !== "ready") return null;
  return isMobile ? (
    <MoreOnScroll list={list} />
  ) : (
    <Pager total={list.total} page={list.page} pageSize={list.pageSize} param={list.pageParam} />
  );
}

function MoreOnScroll<T>({ list }: { list: ServerList<T> }) {
  const { L } = useTexts();
  const sentinel = useRef<HTMLDivElement>(null);
  const loadMore = list.loadMore;
  useEffect(() => {
    const element = sentinel.current;
    if (!element || !list.more) return;
    // En el celular con barra fija la página desplaza dentro de `main` (C27): se mide contra él para pedir antes de llegar.
    const observer = new IntersectionObserver(
      (entries) => entries.some((entry) => entry.isIntersecting) && loadMore(),
      {
        root: pageScroller(),
        rootMargin: "200px",
      },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [list.more, loadMore]);
  if (!list.more) return null;
  return (
    <div ref={sentinel} className={styles.more} role="status">
      {list.loadingMore ? L("ADM_LOADING_MORE") : null}
    </div>
  );
}

/** Números de página a la vista: con más de 7, la primera, la última y las vecinas de la actual, con «…». */
export function pageNumbers(current: number, last: number): (number | "gap")[] {
  if (last <= 7) return Array.from({ length: last }, (_, index) => index + 1);
  const wanted = new Set([1, last, current - 1, current, current + 1].filter((value) => value >= 1 && value <= last));
  const sorted = [...wanted].sort((a, b) => a - b);
  const result: (number | "gap")[] = [];
  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1]! > 1) result.push("gap");
    result.push(value);
  });
  return result;
}

/**
 * C28 · paginado de escritorio: con 10 filas o menos no hay; con más de 10 y una sola página, solo «Por página»; con más
 * de una página, «Por página», el rango «1–10 de 32» y los números (la actual en tinte). Todo en la dirección.
 */
export function Pager({
  total,
  page,
  pageSize,
  param = "page",
}: {
  total: number;
  page: number;
  pageSize: number;
  param?: string;
}) {
  const { L, locale } = useTexts();
  const [params, setParams] = useSearchParams();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menu) return;
    const onDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node))
        setMenu(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenu(false);
      trigger.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);
  // C11 · menú de valores cortos (`menu-por-pagina`): se abre debajo del botón y arriba solo si abajo no entra.
  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!menu || !node) return;
    node.style.removeProperty("top");
    node.style.removeProperty("bottom");
    if (node.getBoundingClientRect().bottom > window.innerHeight - 8) {
      node.style.top = "auto";
      node.style.bottom = "calc(100% + 4px)";
    }
  }, [menu]);
  if (total <= pageSizes[0]) return null;
  const last = Math.max(1, Math.ceil(total / pageSize));
  const number = (value: number) => value.toLocaleString(locale);
  const go = (next: number) => {
    const copy = new URLSearchParams(params);
    if (next <= 1) copy.delete(param);
    else copy.set(param, String(next));
    setParams(copy);
    window.scrollTo({ top: 0 });
  };
  const size = (next: number) => {
    const copy = new URLSearchParams(params);
    copy.delete(param);
    if (next === 10) copy.delete("pageSize");
    else copy.set("pageSize", String(next));
    setParams(copy);
    setMenu(false);
  };
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <nav className={styles.pager} aria-label={L("ADM_PAGER")} data-part="paginado">
      <span className={styles.perPage}>{L("ADM_PER_PAGE")}</span>
      <div className={styles.sizeHost}>
        <button
          ref={trigger}
          type="button"
          className={styles.sizeButton}
          aria-haspopup="menu"
          aria-expanded={menu}
          aria-label={`${L("ADM_PER_PAGE")}: ${pageSize}`}
          onClick={() => setMenu((value) => !value)}
        >
          {pageSize}
          <ChatIcon name="chevron-down" />
        </button>
        {menu ? (
          <div ref={menuRef} className={styles.sizeMenu} role="menu">
            {pageSizes.map((option) => (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={option === pageSize}
                className={styles.sizeOption}
                onClick={() => size(option)}
              >
                <span>{option}</span>
                {option === pageSize ? <ChatIcon name="check" /> : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {last > 1 ? (
        <>
          <span className={styles.range}>{formatText(L("ADM_RANGE"), number(from), number(to), number(total))}</span>
          <button
            type="button"
            className={styles.pageButton}
            aria-label={L("ADM_PAGE_PREVIOUS")}
            title={L("ADM_PAGE_PREVIOUS")}
            disabled={page <= 1}
            onClick={() => go(page - 1)}
          >
            <ChatIcon name="chevron-left" />
          </button>
          {pageNumbers(Math.min(page, last), last).map((value, index) =>
            value === "gap" ? (
              <span key={`gap-${index}`} className={styles.gap} aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={value}
                type="button"
                className={styles.pageButton}
                aria-label={formatText(L("ADM_PAGE_NUMBER"), value)}
                aria-current={value === page ? "page" : undefined}
                onClick={() => go(value)}
              >
                {number(value)}
              </button>
            ),
          )}
          <button
            type="button"
            className={styles.pageButton}
            aria-label={L("ADM_PAGE_NEXT")}
            title={L("ADM_PAGE_NEXT")}
            disabled={page >= last}
            onClick={() => go(page + 1)}
          >
            <ChatIcon name="chevron-right" />
          </button>
        </>
      ) : null}
    </nav>
  );
}
